import { defineStore } from 'pinia'
import env from '~/libs/env'
import { useBorrowerStore } from '~/store/borrower'
import { useRootStore } from '~/store/root'
import jsonHelper from '~/libs/json-helper'
import { get, omit } from 'es-toolkit/compat'

const MONEY_LION_ALT_PRODUCT_ID = 20
const NAV_ALT_PRODUCT_IDS = [29, 35]
const DEPOSIT_PRODUCT_IDS = [39, 40, 41]

// Helper functions
function parseOption(option = {}) {
  if (option.alternateProductContent) {
    return parseAltOptionContent(option)
  }

  if (option.lender_product_content) {
    return parseLenderProductContent(option)
  }

  return {}
}
function parseAltOptionContent(option = {}) {
  const content = option.alternateProductContent
  const json = jsonHelper.safeParse(content.json)
  const returnData = {
    ...json,
    ...omit(content, ['id', 'json', 'alternateProductId', 'featuredData', 'created', 'modified', 'deleted']),
  }

  const featuredData = {}
  if (content.featuredData && typeof content.featuredData === 'object' && !Array.isArray(content.featuredData)) {
    for (const [key, featuredContent] of Object.entries(content.featuredData)) {
      if (!featuredContent) {
        continue;
      }
      featuredData[key] = {
        ...jsonHelper.safeParse(featuredContent.json),
        ...omit(featuredContent, ['id', 'json', 'alternateProductContentId', 'isActive', 'created', 'modified']),
      }
    }
  }
  returnData['featuredData'] = featuredData
  if(json['clickUrl']) returnData['clickUrl'] = json['clickUrl']

  return returnData
}
function parseLenderProductContent(option = {}) {
  const content = option.lender_product_content
  return {
    title: content.lender.name + ' - ' + content.name,
    description: content.category.description,
    ctaType: 'checkbox',
    cta:
      'Yes, I would like to talk with a ' +
      content.lender.name +
      ' representative.',
    imageUrl: content.lender.has_logo
      ? env('lenderLogosBaseUrl') + '/lender-' + content.lender.id + '.png'
      : '/images/lender-logos/lender_logo_generic.png',
    alert:
      'A representative from ' +
      content.lender.name +
      ' will be reaching out to you shortly.'
  }
}
function includeTrackingData(clickUrl, trackingParams = {}) {
  if (!trackingParams || !Object.keys(trackingParams).length) {
    return clickUrl;
  }

  const url = new URL(clickUrl)
  const trackingKeys = Object.keys(trackingParams)
  const params = trackingKeys.map((key) => `${key}=${trackingParams[key]}`).join('&')
  if (!url.search) {
    url.search = `?${params}`
  } else {
    const replaceRegex = new RegExp(`&?(${trackingKeys.join('|')})=([^&]*)&?`, 'g')
    url.search = url.search.replace(replaceRegex, '')

    const searchParts = [params]
    const currentSearch = url.search.replace(/^\?/, '')
    if (currentSearch) {
      searchParts.push(currentSearch)
    }

    url.search = `?${searchParts.join('&')}`
  }

  return url.toString()
}
function prefillMoneyLionFields(borrowerValues = {}) {
  if (!borrowerValues || !Object.keys(borrowerValues).length) {
    return {}
  }
  const loanPurpose = get(borrowerValues, 'loanPurpose.value')
  const requestedAmount = parseInt(get(borrowerValues, 'financeAmount.value'))
  const creditScore = parseInt(get(borrowerValues, 'creditScore.value'))
  const city = get(borrowerValues, 'owner_city.value')
  const state = get(borrowerValues, 'owner_state.value')
  const zipCode = get(borrowerValues, 'owner_zip.value')
  const homeOwnershipType = get(borrowerValues, 'homeOwnershipType.value')
  const annualIncome = parseInt(get(borrowerValues, 'annual_personal_income.value'))

  const data = {}
  if (loanPurpose) {
    const loanPurposeMap = {
      realEstate: 'home_purchase',
      other: 'other'
    }
    data['app.purpose'] = loanPurposeMap[loanPurpose] || 'business'
  }
  if (Number.isFinite(requestedAmount)) {
    data['app.loanAmount'] = requestedAmount
  }
  if (Number.isFinite(creditScore)) {
    const creditScoreCategories = [
      { label: 'excellent', min: 720 },
      { label: 'good', min: 660 },
      { label: 'fair', min: 620 },
      { label: 'poor', min: 0 }
    ];

    data['app.providedCreditRating'] = creditScoreCategories
      .find(category => creditScore >= category.min)
      ?.label || 'unknown'
  }
  if (city) {
    data['app.city'] = city
  }
  if (state) {
    data['app.state'] = state
  }
  if (zipCode) {
    data['app.zipcode'] = zipCode
  }
  if (homeOwnershipType && homeOwnershipType === 'rent') {
    data['app.propertyStatus'] = homeOwnershipType
  }
  if (Number.isFinite(annualIncome)) {
    data['app.annualIncome'] = annualIncome
  }
  return data
}
function prefillBorrowerData(altProductId, borrowerId, borrowerValues = {}) {
  if (!altProductId || !borrowerId || !borrowerValues || !Object.keys(borrowerValues).length) {
    return {}
  }
  switch (true) {
    case altProductId === MONEY_LION_ALT_PRODUCT_ID:
      return prefillMoneyLionFields(borrowerValues) || {}
    case NAV_ALT_PRODUCT_IDS.includes(altProductId):
      return { 'subid1': borrowerId }
    default:
      return {}
  }
}
function getClickUrlBySection(option, section) {
  const sectionMap = {
    'financing-options': option.mpOffersClickUrl,
    'dashboard-featured': option.mpDashboardClickUrl,
    'dashboard-low-credit': option.mpLowCreditClickUrl,
    'dashboard-deposit': option.mpDepositClickUrl,
    'modal': option.mpModalClickUrl,
    'hartford-promo': option.mpFundedPromoClickUrl,
  }
  return sectionMap[section] || option.clickUrl
}
// End helper functions

export const useOptInsStore = defineStore('optIns', () => {
  const { $axios } = useNuxtApp()

  const borrowerStore = useBorrowerStore()
  const rootStore = useRootStore()

  /*
  ███████ ████████  █████  ████████ ███████
  ██         ██    ██   ██    ██    ██
  ███████    ██    ███████    ██    █████
       ██    ██    ██   ██    ██    ██
  ███████    ██    ██   ██    ██    ███████
  STATE
*/
  const allOptions = ref([])
  const options = ref([]) // non-financing options (e.g., credit cards)
  const financingOptions = ref([])
  const loading = ref(false)
  const leadId = ref(null)
  const error =  ref(null)
  const loaded = ref(false)

  /*
   ██████  ███████ ████████ ████████ ███████ ██████  ███████
  ██       ██         ██       ██    ██      ██   ██ ██
  ██   ███ █████      ██       ██    █████   ██████  ███████
  ██    ██ ██         ██       ██    ██      ██   ██      ██
   ██████  ███████    ██       ██    ███████ ██   ██ ███████
  GETTERS
*/
  const isNavOption = computed(() => {
    return (option) => NAV_ALT_PRODUCT_IDS.includes(option?.id)
  })

  const depositOption = computed(() => {
    return (allOptions.value || []).find(option => DEPOSIT_PRODUCT_IDS.includes(option?.id))
  })

  /*
   █████   ██████ ████████ ██  ██████  ███    ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ████   ██ ██
  ███████ ██         ██    ██ ██    ██ ██ ██  ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ██  ██ ██      ██
  ██   ██  ██████    ██    ██  ██████  ██   ████ ███████
  ACTIONS
  ! - - Actions calling other actions in the same store must use `this.actionName(...)`
  ! - - If we do not use `this.actionName` it will not be properly mockable in tests.
  ! - - Computeds and refs will work fine, and should be called directly though.
*/

function setOptInsSuccess(_allOptions, addMore = false) {
  let allData = _allOptions
  if (!addMore) {
    loading.value = false
  } else {
    allData = [...(allOptions.value || []), ..._allOptions]
  }

  allOptions.value = allData

  financingOptions.value = allData.filter(
    (option) => option.productType === 'financing'
  )
  options.value = allData.filter(
    (option) => option.productType !== 'financing'
  )
}

async function optInsSuccess(cards, addMore = false) {
  let _allOptions = cards.map((option) =>
    Object.assign({ markingInterested: false }, option, parseOption(option))
  )

  // if borrower.stateId is NC, OR exclude Lexington Law from them
  if (
    borrowerStore.borrower &&
    borrowerStore.borrower.stateId
  ) {
    if (
      ['NC', 'OR'].includes(
        borrowerStore.borrower.stateId
      )
    ) {
      const LEXINGTON_LAW_ALT_OPTION_ID = 2
      _allOptions = _allOptions.filter(
        (o) => o.id !== LEXINGTON_LAW_ALT_OPTION_ID
      )
    }
  }

  this.setOptInsSuccess(_allOptions, addMore)
}

  async function getOptInOffers(force = false) {
    if (options.value && financingOptions.value && loaded.value && !force) {
      return
    }

    if (!borrowerStore.borrowerId) {
      log.error(
        'opt-in could not be retrieved because of null or undefined borrowerId on rootState',
        {
          borrowerId: borrowerStore.borrowerId,
          borrower: borrowerStore.borrower
        }
      )
      return
    }

    loading.value = true
    await $axios
      .get(
        `${env('apiUrl')}/alt-option-list/${
          borrowerStore.borrowerId
        }`,
      )
      .then(async (res) => {
        await this.optInsSuccess(Object.values(res.data.data))
        loaded.value = true
      })
      .catch((err) => {
        loading.value = false
        error.value = err
        return
      })
  }

    function markInterested(altProductId) {
      const option = options.value.find((option) => option.id === altProductId)
      if (option) {
        option.markingInterested = true
      }

      const financingOption = financingOptions.value.find(
        (option) => option.id === altProductId
      )
      if (financingOption) {
        financingOption.markingInterested = true
      }
    }

    function clickedOnCallToAction(altProductId, data) {
      const clickedTime = data?.clickedOnCallToAction ?? (new Date()).toISOString()
      const option = options.value.find((option) => option.id === altProductId)
      if (option) {
        option.clickedOnCallToAction = true
        option.clickedAt = clickedTime
      }

      const financingOption = financingOptions.value.find(
        (option) => option.id === altProductId
      )
      if (financingOption) {
        financingOption.clickedOnCallToAction = true
        financingOption.clickedAt = clickedTime
      }
    }

    function finishedMarkingInterested(altProductId) {
      const option = options.value.find((option) => option.id === altProductId)
      if (option) {
        option.markingInterested = false
      }

      const financingOption = financingOptions.value.find(
        (option) => option.id === altProductId
      )
      if (financingOption) {
        financingOption.markingInterested = false
      }
    }

    async function interested(altProductId, displaySection = 'explore', trackingData = {}, openInNewTab = true) {
      const trackingProps = {
        ...(trackingData && Object.keys(trackingData).length ? trackingData : {}),
        ...prefillBorrowerData(altProductId, borrowerStore.borrowerId, borrowerStore.borrowerValues)
      }
      const option = options.value.find((option) => option.id === altProductId)
      const financingOption = financingOptions.value.find(
        (option) => option.id === altProductId
      )
      this.markInterested(altProductId)

      let hasStaticUrl = false
      const openLink = (url) => {
        if (openInNewTab) {
          window.open(url, '_blank')
        } else {
          window.location.href = url
        }
      }
      if (option && option.clickUrl) {
        const clickUrl = getClickUrlBySection(option, displaySection)
        hasStaticUrl = true
        openLink(includeTrackingData(clickUrl, trackingProps))
      }
      if (financingOption && financingOption.clickUrl) {
        const clickUrl = getClickUrlBySection(option, displaySection)
        hasStaticUrl = true
        openLink(includeTrackingData(clickUrl, trackingProps))
      }

      const offer = option || financingOption
      const hasAlert = Boolean(offer && offer.alert)
      let newTab
      if (!hasStaticUrl && !hasAlert && openInNewTab) {
        newTab = window.open('', '_blank')
      }
      const closeTab = () => {
        if (!newTab) {
          return;
        }
        newTab.close()
        newTab = null
      }

      const data = {
        leadId: leadId.value,
        displaySection,
      }
      await $axios
        .post(
          `${env('apiUrl')}/alt-option-interested/${altProductId}/borrower/${
            borrowerStore.borrowerId
          }`,
          JSON.stringify(data),
          { headers: { 'Content-Type': 'application/json' } }
        )
        .then((res) => {
          const altOptInterested = res?.data?.data
          this.clickedOnCallToAction(altProductId, altOptInterested)

          if (newTab) {
            if (altOptInterested?.clickUrl) {
              newTab.location.href = altOptInterested.clickUrl
            } else {
              closeTab()
            }
          } else if (!openInNewTab && altOptInterested?.clickUrl) {
            window.location.href = altOptInterested.clickUrl
          }
        })
        .catch((err) => {
          closeTab()
          if (err.response || !err.isAxiosError) {
            log.error('Failed to mark opt-in as interested', {
              altProductId,
              err
            })
          }
        })

      this.finishedMarkingInterested(altProductId)
    }

    async function viewed(altProductId) {
      $axios
        .post(`${env('apiUrl')}/alt-option-view/${altProductId}`)
        .catch((err) => {
          if (err.response && err.response.status >= 500) {
            log.error(`Failed to mark opt-in as viewed`, { altProductId, err })
          }
        })
    }

    async function addOptions(options) {
      const existingIds = (allOptions.value || []).map((opt) => opt.id)
      const newOptions = (options || []).filter(opt => !existingIds.includes(opt.id))

      if (newOptions.length) {
        await this.optInsSuccess(newOptions, true)
      }
    }

    function reset() {
      allOptions.value = []
      options.value = []
      financingOptions.value = []
      loading.value = false
      error.value =  null
      loaded.value = false
    }

    return {
      //STATE
      allOptions,
      options, // non-financing options (e.g., credit cards)
      financingOptions,
      loading,
      leadId,
      error,
      loaded,

      //GETTERS
      isNavOption,
      depositOption,

      //ACTIONS
      setOptInsSuccess,
      optInsSuccess,
      getOptInOffers,
      markInterested,
      clickedOnCallToAction,
      finishedMarkingInterested,
      interested,
      viewed,
      addOptions,
      reset,
    }
})
